import React, { useEffect } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { tr } from "../lib/i18n";

export const HOME_FAQS = [
  {
    q: "How much does it cost to start a forex brokerage?",
    a: "Company incorporation starts from $6,500 and a full brokerage launch from $35,000, with fast-track incorporation completing in 7–10 days. Third-party costs — platform, liquidity, payment providers — are itemized in a written budget before any engagement starts.",
  },
  {
    q: "Can I start a brokerage or prop firm with a small budget?",
    a: "Yes — with staging. Most smaller founders start with an offshore entity from $6,500 or a complete prop firm launch from $18,000, then scale the platform, liquidity and licensing as revenue justifies it. We build the launch around your budget, not the other way round.",
  },
  {
    q: "How much does a forex license cost?",
    a: "It depends on the jurisdiction: Saint Lucia and SVG use incorporation-first routes with no local forex license requirement, while Seychelles and Mauritius licenses carry capital requirements and higher running costs. We map the cheapest structure that actually works for your target markets before you spend.",
  },
  {
    q: "How long does it take to launch?",
    a: "Fast-track incorporation completes in 7–10 days. A full stack — platform, liquidity, CRM, payments — is typically live in a matter of weeks depending on providers and licensing scope.",
  },
  {
    q: "Do you work with founders in Asia, Africa and Latin America?",
    a: "Yes — the majority of our launches come from Southeast Asia, India, Africa and LATAM. We operate from the UAE since 2015, with a Spanish-speaking team and payment stacks built for how traders in these regions actually pay.",
  },
  {
    q: "Who is Ah-Jin Group?",
    a: "Ah-Jin Group is a brokerage and prop-firm launch consultancy founded in 2015, working from the UAE and Cyprus with founders worldwide — especially in Latin America, Africa, India and Southeast Asia. One accountable team handles company incorporation, licensing, trading platforms (MT5, cTrader, Match-Trader), liquidity, CRM, payments and compliance, from idea to a live business. Engagements start with a paid $250, 30-minute Founder Strategy Call that is credited toward your project.",
  },
  {
    q: "Do you provide compliance support, MLRO and AML services for brokers?",
    a: "Yes — through our associates. We arrange AML and KYC policies, MLRO and DMLRO cover, client agreements, risk warnings, compliance calendars and regulator liaison for forex brokerages and prop trading firms, so your company is bank-ready and audit-ready from day one. Ongoing compliance support is scoped separately from the launch.",
  },
  {
    q: "Can you open a corporate bank account and card payment processing for a new broker?",
    a: "Yes. We prepare a bank-ready corporate file, introduce you to banks and EMIs that accept trading businesses, and match you with card payment processors and PSPs (acquiring, 3-DS, chargeback controls) plus crypto gateways — in the order that gets you accepting deposits fastest. Payments and banking are planned with incorporation, never after launch.",
  },
  {
    q: "What changed in prop firm regulation in 2026?",
    a: "There is still no global prop-firm rulebook, but 2026 split the map: the US moved toward CFTC/NFA-style oversight of funded-trader programs, ESMA said retail prop trading is not an immediate priority while national regulators keep testing the MiFID II perimeter, and the UK applies financial-promotion rules rather than authorisation. New prop firms should launch with clear simulated-trading disclosures, a payout reserve, KYC before first payout and US-marketing controls.",
  },
  {
    q: "Did Seychelles change its forex license requirements?",
    a: "Yes. The Seychelles securities dealer licence now requires USD 100,000 paid-up capital (up from USD 50,000), the annual fee doubled to USD 6,000, and a corporate governance code applies from January 2026. For founders with smaller budgets, Saint Lucia or SVG incorporation-first routes remain the realistic entry point, with a licence added later from revenue.",
  },
  {
    q: "What is the $250 Strategy Call?",
    a: "A 30-minute session with a senior consultant that ends with a written launch map: jurisdiction, structure, realistic all-in costs and timeline. The full $250 is credited toward any engagement — so when you proceed, the call effectively costs nothing.",
  },
];

const HomeFaq = () => {
  useEffect(() => {
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "home-faq-jsonld";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: HOME_FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(ld);
    return () => {
      const el = document.getElementById("home-faq-jsonld");
      if (el) el.remove();
    };
  }, []);

  return (
    <section className="relative py-16 sm:py-20 overflow-hidden border-t border-white/[0.06]" data-testid="home-faq-section">
      <div className="absolute inset-0 bg-[#081024]/60" />
      <div className="relative max-w-4xl mx-auto px-5 sm:px-10">
        <div className="text-[10px] tracking-[0.45em] uppercase text-[#E5C56F]">
          {tr("Questions founders ask")}
        </div>
        <h2 className="font-display mt-3 text-2xl sm:text-3xl text-zinc-100">
          {tr("Straight answers, before you spend")}
        </h2>
        <Accordion type="single" collapsible className="mt-7" data-testid="home-faq-accordion">
          {HOME_FAQS.map((f, i) => (
            <AccordionItem key={f.q} value={`hfaq-${i}`} className="border-white/10">
              <AccordionTrigger
                className="text-left text-sm sm:text-base text-zinc-100 hover:text-purple-200 hover:no-underline"
                data-testid={`home-faq-q-${i}`}
              >
                {tr(f.q)}
              </AccordionTrigger>
              <AccordionContent className="text-[13px] sm:text-sm text-zinc-400 leading-relaxed">
                {tr(f.a)}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

export default HomeFaq;
